"use client";

import type { FormEvent, RefObject } from "react";
import { useTranslations } from "use-intl";

import { CloseIcon, SearchIcon } from "@/components/appIcons";

export function SearchInputHeader({
  query,
  inputRef,
  inputValue,
  setInputValue,
  clearInput,
  onSubmit,
  browseHeading,
  resultCount,
}: {
  query: string;
  inputRef: RefObject<HTMLInputElement | null>;
  inputValue: string;
  setInputValue: (value: string) => void;
  clearInput: () => void;
  onSubmit: (event: FormEvent) => void;
  browseHeading: string | null;
  resultCount: number | null;
}) {
  const t = useTranslations("globals.search");
  const heading = browseHeading ?? (query ? t("queryHeading", { query }) : t("defaultHeading"));

  return (
    <>
      <form onSubmit={onSubmit} className="mb-6">
        <div className="flex items-stretch rounded-xl h-12 md:h-14 bg-secondary border border-border focus-within:border-primary focus-within:ring-1 focus-within:ring-primary/50 transition-colors">
          <div className="text-muted-foreground flex items-center justify-center pl-4">
            <SearchIcon className="text-xl" />
          </div>
          <input
            ref={inputRef}
            type="text"
            aria-label={t("searchPlaceholder")}
            value={inputValue}
            onChange={(event) => setInputValue(event.target.value)}
            placeholder={t("searchPlaceholder")}
            className="flex w-full min-w-0 flex-1 border-none bg-transparent focus:outline-none focus:ring-0 placeholder:text-muted-foreground px-4 text-base md:text-lg font-normal"
          />
          {inputValue && (
            <button
              type="button"
              aria-label={t("reset")}
              onClick={clearInput}
              className="text-muted-foreground hover:text-foreground flex items-center pr-4"
            >
              <CloseIcon className="text-lg" aria-hidden="true" />
            </button>
          )}
        </div>
      </form>
      <div className="mb-6 max-w-3xl">
        <h1 className="text-2xl font-black leading-tight md:text-3xl">{heading}</h1>
        {resultCount !== null && (
          <p className="mt-1 text-sm text-muted-foreground">
            {t("resultsFound", { count: resultCount })}
          </p>
        )}
      </div>
    </>
  );
}
